import { __awaiter } from "tslib";
import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ApolloError } from 'apollo-boost';
import { useCheckOrderAvailabilityMutation, OrderStatus, useSaveOrderServicesMutation, useCheckinInfoQuery, useGetServicesRefundInfoQuery, useOrderQuery } from '@websky/graphql';
import { getOrder, getOrderId, getRecheckAvailabilityTimout } from './store/order/selectors';
import { fillOrder, setOrder } from './store/order/actions';
import { setMultipleSelectedServices, removeServiceAction } from './store/selectedServices/actions';
import { useIataCode } from '../hooks';
import { calculateCheckinAvailability } from './utils';
import { getPassengersSelectedProductIds } from '../RefundSummary/utils';
import { addLoadingServiceParam, clearLoadingServicesParams } from './store/loadingServicesParams/actions';
import { LoadingServiceType } from './store/loadingServicesParams/reducers';
export const useAvailabilityRecheck = (setActualizationOpen) => {
    const timeoutToReCheckAvailability = useSelector(getRecheckAvailabilityTimout) * 1000;
    const orderId = useSelector(getOrderId);
    const orderStatus = useSelector(getOrder).status;
    const [timeout, setNewTimeout] = useState(null);
    const dispatch = useDispatch();
    const needToCheckAvailability = orderStatus === OrderStatus.New || orderStatus === OrderStatus.Booked;
    const [recheckAvailability, { data }] = useCheckOrderAvailabilityMutation({ fetchPolicy: 'no-cache' });
    const recheckIfTimoutExceed = () => {
        if (needToCheckAvailability) {
            recheckAvailability({ variables: { id: orderId } });
        }
    };
    useEffect(() => {
        var _a, _b;
        if (!(data === null || data === void 0 ? void 0 : data.CheckOrderAvailability)) {
            return () => setActualizationOpen(false);
        }
        if (((_a = data === null || data === void 0 ? void 0 : data.CheckOrderAvailability) === null || _a === void 0 ? void 0 : _a.status) === OrderStatus.Confirmed) {
            clearTimeout(timeout);
            setNewTimeout(null);
            return () => setActualizationOpen(false);
        }
        if (((_b = data === null || data === void 0 ? void 0 : data.CheckOrderAvailability) === null || _b === void 0 ? void 0 : _b.status) === OrderStatus.Cancelled) {
            clearTimeout(timeout);
            setNewTimeout(null);
            return () => setActualizationOpen(false);
        }
        dispatch(fillOrder(data.CheckOrderAvailability));
        clearTimeout(timeout);
        setNewTimeout(null);
        if (timeoutToReCheckAvailability === 0) {
            setNewTimeout(setTimeout(recheckIfTimoutExceed, timeoutToReCheckAvailability));
        }
        setActualizationOpen(true);
        return () => {
            setActualizationOpen(false);
        };
    }, [timeoutToReCheckAvailability]);
    useEffect(() => {
        if (!timeout) {
            setNewTimeout(setTimeout(recheckIfTimoutExceed, timeoutToReCheckAvailability));
        }
    }, [timeout, timeoutToReCheckAvailability]);
};
export const useIsCheckinAvailable = (order) => {
    var _a;
    const isConfirmed = order.status === OrderStatus.Confirmed;
    const iataCode = useIataCode();
    const manualCheckIataCodes = ['DV'];
    const { data: getCheckinInfoResponse } = useCheckinInfoQuery({
        skip: !isConfirmed || manualCheckIataCodes.includes(iataCode),
        variables: {
            params: {
                aviaOrderId: order.id
            }
        }
    });
    if (manualCheckIataCodes.includes(iataCode) && isConfirmed) {
        return calculateCheckinAvailability(order);
    }
    return (_a = getCheckinInfoResponse === null || getCheckinInfoResponse === void 0 ? void 0 : getCheckinInfoResponse.CheckinInfo) === null || _a === void 0 ? void 0 : _a.isAvailable;
};
export const useServicesRefundInfo = (orderId, travellers, selectedPassengerIds, selectedSegmentIds, isAllFlightToExchange, skip) => {
    const productIds = React.useMemo(() => {
        return getPassengersSelectedProductIds(travellers, selectedPassengerIds, selectedSegmentIds, isAllFlightToExchange);
    }, [travellers, selectedPassengerIds, selectedSegmentIds, isAllFlightToExchange]);
    return useGetServicesRefundInfoQuery({
        variables: { params: { orderId, productIds } },
        skip: !productIds.length || skip
    });
};
export function useOrderServices() {
    const dispatch = useDispatch();
    const [saveOrderServices, saveOrderServicesResult] = useSaveOrderServicesMutation();
    const [requestError, setRequestError] = useState();
    // Effects
    useEffect(() => {
        var _a;
        if ((_a = saveOrderServicesResult === null || saveOrderServicesResult === void 0 ? void 0 : saveOrderServicesResult.data) === null || _a === void 0 ? void 0 : _a.SaveOrderServices) {
            dispatch(fillOrder(saveOrderServicesResult.data.SaveOrderServices));
        }
    }, [saveOrderServicesResult === null || saveOrderServicesResult === void 0 ? void 0 : saveOrderServicesResult.data]);
    // Methods
    const onRequestError = useCallback((e) => {
        var _a;
        if (e instanceof ApolloError) {
            if ((_a = e.graphQLErrors) === null || _a === void 0 ? void 0 : _a.length) {
                setRequestError(e.graphQLErrors[0].message);
            }
        }
        else if (e instanceof Error) {
            setRequestError(e.message);
        }
        else {
            setRequestError('Unknown error');
        }
    }, [setRequestError]);
    const onClearError = useCallback(() => setRequestError(undefined), [setRequestError]);
    const onSaveServicesRequest = useCallback((params) => __awaiter(this, void 0, void 0, function* () {
        let type;
        if (params.insurances) {
            type = LoadingServiceType.Insurance;
        }
        else if (params.aeroexpress) {
            type = LoadingServiceType.Transfer;
        }
        if (type) {
            dispatch(addLoadingServiceParam({ type: [type] }));
        }
        try {
            const { data } = yield saveOrderServices({ variables: { params } });
            return data === null || data === void 0 ? void 0 : data.SaveOrderServices;
        }
        catch (e) {
            onRequestError(e);
            return null;
        }
        finally {
            if (type) {
                dispatch(clearLoadingServicesParams());
            }
        }
    }), [onRequestError, dispatch]);
    const onAddServices = useCallback((services) => {
        dispatch(setMultipleSelectedServices(services));
    }, [dispatch]);
    const onRemoveServices = useCallback((services) => {
        services.forEach(serviceToDelete => dispatch(removeServiceAction(serviceToDelete)));
    }, [dispatch]);
    return {
        // data
        requestError,
        saveOrderServicesResult,
        isLoading: saveOrderServicesResult.loading,
        // methods
        saveOrderServices,
        onRequestError,
        onClearError,
        onSaveServicesRequest,
        onAddServices,
        onRemoveServices
    };
}
export function useOrderUpdate() {
    const dispatch = useDispatch();
    const orderId = useSelector(getOrderId);
    const { refetch: getOrder, loading } = useOrderQuery({
        variables: { id: orderId },
        fetchPolicy: 'no-cache',
        skip: true
    });
    const onFetchOrder = () => __awaiter(this, void 0, void 0, function* () {
        try {
            const { data } = yield getOrder();
            return data;
        }
        catch (_a) {
            return null;
        }
    });
    const onRefreshOrder = () => __awaiter(this, void 0, void 0, function* () {
        const order = yield onFetchOrder();
        if (order === null || order === void 0 ? void 0 : order.Order) {
            dispatch(setOrder(order.Order));
        }
        return order;
    });
    return {
        loading,
        onFetchOrder,
        onRefreshOrder
    };
}
